import { FC, useEffect, useState } from "react";
import { CopaymentEditBillInfoProps } from "../types/CopaymentEdit.types";
import { useCopaymentEditBillInfo } from "../hooks/useCopaymentEdit";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";
import { BillingStepper } from "../../../../Base/components/Stepper";
import {
  A5PrintComponent,
  BillCardList,
  ExportConfirmationDialog,
  InputTestTypeDialog,
  TaxierenConfirmationDialog,
} from "../../../DirectBilling/Edit/exports";
import { TestType } from "../../../DirectBilling/Edit/types/TestTypes.interface";
import { DeleteConfirmationDialog } from "../../../DirectBilling/common/exports";
import A4PrintComponent from "../../../DirectBilling/Edit/components/helper-components/A4PrintComponent";
import { CopaymentHeaderButtons } from "./CopaymentHeaderButtons";
import { useBillSummary } from "../../../Base/hooks/useBillSummary";
import BillSummary from "../../../DirectBilling/NewBill/components/sub-components/BillSummary";
import { Delete } from "@mui/icons-material";

const CopaymentEditBillInfo: FC<CopaymentEditBillInfoProps> = (props) => {
  const {
    // states
    anchorEl,
    openDeleteDialog,
    bills,
    notes,
    openExportDialog,
    selectedRxId,
    openTaxierenDialog,
    printData,
    isPrinting,
    isDrucken,
    printProgress,
    printRef,
    isExportLoading,
    todayString,
    isSoftDeleteDialogOpen,
    testToggleChecked,
    openTestTypeDialog,
    testTypeData,
    billsChanged,

    // handlers
    setOpenCancelTaxierenDialog,
    setOpenExportDialog,
    setSelectedRxId,
    setNotes,
    setTestTypeData,
    setTestToggleChecked,
    handleMenuOpen,
    handleMenuClose,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleTaxierenClick,
    handleCancelTaxierenConfirm,
    // handleAddSingleRx,
    handleValidationFilter,
    handleSave,
    handleDrucken,
    handleExport,
    handleExportConfirm,
    handleSoftDeleteCancel,
    handleSoftDeleteConfirm,
    handleTestToggleChange,
    handleTestTypeDialogClose,

    // from props
    onRowClick,
    onRemoveAll,
  } = useCopaymentEditBillInfo(props);

  const { totalRxs, passedValidationRxs, problematicRxs, taxiertRxs, numberOfPayers } =
    useBillSummary({ bills });

  // State for stepper
  const [activeStep, setActiveStep] = useState(0);

  // If user edits bills after step 0, revert back to step 0
  useEffect(() => {
    if (billsChanged && activeStep > 0) {
      setActiveStep(0);
    }
  }, [billsChanged, activeStep]);

  const handleSaveClicked = async () => {
    await handleSave();
    setActiveStep(1);
  };

  const handleDruckenClicked = async () => {
    await handleDrucken(testTypeData.type);
    setActiveStep(2);
  };

  const handleExportClicked = async () => {
    await handleExport();
  };

  const handleNotesChange = (billId: string, noteValue: string) => {
    setNotes((prevNotes) => ({
      ...prevNotes,
      [billId]: noteValue,
    }));
  };

  const backdropMessage = isDrucken ? "Es wird erstellt..." : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Backdrop for printing */}
      <WaitingBackdrop
        isWorking={isPrinting || isDrucken}
        message={backdropMessage}
        progress={isDrucken ? printProgress : undefined}
      />

      {/* Header Buttons */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "#5c758a",
          borderRadius: "0px 0px 10px 10px",
        }}
      >
        <Box sx={{ mt: 1 }}>
          <BillingStepper activeStep={activeStep} />
        </Box>
        <CopaymentHeaderButtons
          handleValidationFilter={handleValidationFilter}
          // ============ NEW HANDLERS TO INTEGRATE STEPPERS============
          handleSave={handleSaveClicked}
          handleDrucken={handleDruckenClicked}
          handleExport={handleExportClicked}
          /// ============ END NEW HANDLERS ============
          testToggleChecked={testToggleChecked}
          handleTestToggleChange={handleTestToggleChange}
          testTypeData={testTypeData}
        />
        <Box sx={{ px: 1 }}>
          <BillSummary
            totalRxs={totalRxs}
            passedValidationRxs={passedValidationRxs}
            problematicRxs={problematicRxs}
            taxiertRxs={taxiertRxs}
            numberOfPayers={numberOfPayers}
          />
        </Box>
      </Box>

      {/* BILLS */}
      <BillCardList
        bills={bills}
        todayString={todayString}
        selectedRxId={selectedRxId}
        notes={notes}
        setNotes={handleNotesChange}
        onRowClick={onRowClick}
        setSelectedRxId={setSelectedRxId}
        handleMenuOpen={handleMenuOpen}
        handleTaxierenClick={handleTaxierenClick}
        onRemoveAll={onRemoveAll}
      />

      {/* Export Confirmation Dialog */}
      <ExportConfirmationDialog
        open={openExportDialog}
        onClose={() => {
          setOpenExportDialog(false);
        }}
        onConfirm={() => {
          if (testToggleChecked) {
            handleExportConfirm(testTypeData); // If user is in test mode, pass the testType, If not in test mode, pass nothing => defaults to GLOBAL
          } else {
            handleExportConfirm({ type: TestType.GLOBAL, email: undefined });
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      {/* Soft Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={isSoftDeleteDialogOpen}
        onCancel={handleSoftDeleteCancel}
        onConfirm={handleSoftDeleteConfirm}
        title="Transaktionsbestätigung löschen"
        content="Dies ist das letzte Element. Möchten Sie die Transaktion wirklich löschen?"
      />

      {/* Taxieren Confirmation Dialog */}
      <TaxierenConfirmationDialog
        open={openTaxierenDialog}
        onClose={() => {
          setOpenCancelTaxierenDialog(false);
        }}
        onConfirm={handleCancelTaxierenConfirm}
      />

      {/* Print Component For Taxiren*/}
      {printData && (
        <div style={{ display: "none" }}>
          {printData.isZahnarzt ? (
            <A4PrintComponent
              ref={printRef}
              billNumber={printData.billNumber}
              rxNumber={printData.rxNumber}
              careProviderIk={printData.careProviderIk}
            />
          ) : (
            <A5PrintComponent
              ref={printRef}
              billNumber={printData.billNumber}
              rxNumber={printData.rxNumber}
              careProviderIk={printData.careProviderIk}
            />
          )}
        </div>
      )}

      {/* Test Type Dialog - For Test Mode Only */}
      <InputTestTypeDialog
        isOpen={openTestTypeDialog}
        onClose={() => {
          setTestToggleChecked(false);
          handleTestTypeDialogClose();
        }}
        onConfirm={(chosenType, chosenEmail) => {
          setTestTypeData({ type: chosenType, email: chosenEmail });
          handleTestTypeDialogClose();
        }}
      />

      {/* Backdrop for export */}
      {isExportLoading && (
        <WaitingBackdrop isWorking={isExportLoading} message="Bitte warten Sie. Exportieren... " />
      )}

      {/* Kebab Menu (Actions) */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteClick}>
          {" "}
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          <ListItemText>Entfernen</ListItemText>
        </MenuItem>
        {/* <MenuItem onClick={handleCancelTaxierenClick}><ListItemIcon><ApprovalRounded/></ListItemIcon><ListItemText>Untaxieren</ListItemText></MenuItem> */}
      </Menu>
    </Box>
  );
};

export default CopaymentEditBillInfo;
